import { Flex, FlexProps, Tag, TagCloseButton, TagLabel } from "@chakra-ui/react";

import { getContrastingColor } from "@/utils/get-contrasting-color";
import { stringToHexColor } from "@/utils/string-to-hex-color";

type RanaTagsLayout = "shrink" | "wrap";

interface RanaTagsProps extends FlexProps {
  tags: string[];
  onRemove?: (tag: string) => void;
  /**
   * - `shrink` (default): one row; tags shrink to share the width and the hovered tag expands.
   * - `wrap`: tags keep their full size and flow onto multiple rows.
   */
  layout?: RanaTagsLayout;
}

const layoutStyles: Record<RanaTagsLayout, { container: FlexProps; tagWrapper: FlexProps }> = {
  // Wrapping is done by the container; tags keep their natural size, so they need no extra styles.
  wrap: {
    container: { gap: 2, flexWrap: "wrap" },
    tagWrapper: {},
  },
  // The container stays a single row; shrinking is done by the tags, so the container needs no extra styles.
  shrink: {
    container: {},
    tagWrapper: {
      flex: 1,
      px: 1,
      _first: { pl: 0 },
      _last: { pr: 0 },
      _hover: { flex: 99999 },
    },
  },
};

export const RanaTags = ({ tags, onRemove, layout = "shrink", ...rest }: RanaTagsProps) => {
  if (!tags.length) {
    return null;
  }

  const styles = layoutStyles[layout];

  return (
    <Flex maxW="full" w="fit" {...styles.container} {...rest} minW={0}>
      {tags.map((tag) => {
        const backgroundColor = stringToHexColor(tag);
        const contrastColor = getContrastingColor(backgroundColor);
        const textColor = contrastColor === "black" ? "brand.keep_deep_sea" : "white"; // Use our default text color instead of black
        return (
          <Flex minW={0} h="fit" {...styles.tagWrapper} maxW="fit" key={tag}>
            <Tag
              position="relative"
              left="1/2"
              transform="translateX(-50%)"
              borderRadius="full"
              overflow="hidden"
              variant="solid"
              bg={backgroundColor}
              color={textColor}
              title={tag}
              onClick={onRemove ? () => onRemove(tag) : undefined}
            >
              <TagLabel>{tag}</TagLabel>
              {onRemove && <TagCloseButton onClick={() => onRemove(tag)} />}
            </Tag>
          </Flex>
        );
      })}
    </Flex>
  );
};
